import React, { useState, useEffect, useRef } from "react";
import TrainService from "../services/TrainService";
import PreliminaryReservationService from "../services/PreliminaryReservationService";
import Wagon from "./Wagon";
import ReservationInfo from "./ReservationInfo";
import {Link, useParams} from "react-router-dom";
import {useSelector, useDispatch} from "react-redux";
import {setSessionId, clearSessionId} from "../actions/session";
import {removeSeat} from "../actions/reservedSeats";
import useBookingStepGuard from "../hooks/useBookingStepGuard";

const Train = (props) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const stepReady = useBookingStepGuard('seats');

    const initialTrainState = {
        id: null,
        carriages: [],
        name: "",
        description: "",
        number: "",
        preliminaryReservedSeats: []
    };
    const [currentTrain, setCurrentTrain] = useState(initialTrainState);

    const getTrain = id => {
        TrainService.get(id)
            .then(response => {
                setCurrentTrain(response.data.data);
            })
            .catch(e => {
                console.log(e);
            });
    };

    useEffect(() => {
        getTrain(props.match.params.id);
    }, [props.match.params.id]);

    const { seats } = useSelector(state => state.activeSeats);
    const { travelers } = useSelector(state => state);
    const sessionId = useSelector(state => state.session);
    const [errorMessage, setErrorMessage] = useState(null);
    const isRemovingFailedSeats = useRef(false);

    useEffect(() => {
        if (!seats || seats.length === 0 || !currentTrain.id || isRemovingFailedSeats.current) {
            return;
        }

        const timeoutId = setTimeout(() => {
            PreliminaryReservationService.reserve(currentTrain.id, seats, sessionId)
                .then(response => {
                    // Reserved seats are connected to a unique session id for each visitor
                    if (!sessionId && response.data.session_id) {
                        dispatch(setSessionId(response.data.session_id));
                    }

                    // Seat(s) could not be reserved for some reason
                    if (response.data.errors && response.data.errors.length > 0) {
                        const errorSeats = response.data.errors.map(e => `${e.seat_number}`).join(', ');
                        setErrorMessage(`Plats ${errorSeats} kunde inte reserveras - den är upptagen av annan användare.`);
                        setTimeout(() => setErrorMessage(null), 5000);
                        isRemovingFailedSeats.current = true;
                        response.data.errors.forEach(error => {
                            dispatch(removeSeat(error.carriage_id, error.seat_number));
                        });

                        setTimeout(() => {
                            isRemovingFailedSeats.current = false;
                        }, 100);
                    } else {
                        // Clear any previous errors if all seats reserved successfully
                        setErrorMessage(null);
                    }

                    // Refresh train data to get updated preliminary reservations
                    getTrain(currentTrain.id);
                })
                .catch(e => {
                    console.error("Failed to reserve seats preliminarily:", e);
                });
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [seats, currentTrain.id, sessionId]);

    // Filter out current user's preliminary reservations (so they can deselect their own seats)
    const otherUsersPreliminarySeats = currentTrain.preliminaryReservedSeats
        ? currentTrain.preliminaryReservedSeats.filter(seat => seat.sessionId !== sessionId)
        : [];

    // After a reload the station/travelers state is gone; the guard redirects
    // to the step that rebuilds it instead of rendering a broken page.
    if (!stepReady) {
        return null;
    }

    return (
        <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
                {currentTrain && (
                    <>
                        <div className="px-4 pt-5 sm:pt-6">
                            <h3 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">Välj sittplats</h3>

                            {errorMessage && (
                                <div className="rounded-md bg-yellow-50 p-4 mb-4 mt-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-yellow-700">
                                                {errorMessage}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="content">
                                {currentTrain.carriages &&
                                currentTrain.carriages.map((carriage, index) => (
                                        <React.Fragment key={index}>
                                            <Wagon key={carriage.id} id={carriage.id} series={carriage.class} number={carriage.number} announcedNumber={carriage.announcedNumber} reservedSeats={currentTrain.reservedSeats} preliminaryReservedSeats={otherUsersPreliminarySeats} />
                                        </React.Fragment>
                                ))}
                            </div>
                        </div>
                        <div className="px-4 pb-2 flex items-center justify-center w-full">
                            <div className="scroll-hint">
                                <svg xmlns="http://www.w3.org/2000/svg" className="mr-3 h-5 w-5 text-gray-400" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M11 17l-5-5m0 0l5-5m-5 5h12"/>
                                </svg>
                            </div>
                            <div className="text-sm text-gray-500">Scrolla för fler vagnar/platser</div>
                            <div className="scroll-hint">
                                <svg xmlns="http://www.w3.org/2000/svg" className="mr-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </div>
                        </div>
                        <div className="px-4 pb-2 sm:pb-2 flex items-center justify-center w-full">
                            <div className="w-6 h-6 mr-2 first-class">
                            </div>
                            <div className="text-sm text-gray-500">Platser i 1-klass visas mot gul bakgrund</div>
                        </div>
                    </>
                )}

                <div className="mt-6">
                    {travelers && (
                        <>
                            {seats.length === travelers['total'] && seats.length > 0 && (
                                <Link to={"/trains/" + id + '/book'}
                                      className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Fortsätt
                                </Link>
                            )}
                            {seats.length !== travelers['total'] && (
                                <div className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    Fortsätt
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
                <ReservationInfo />
            </div>
        </>
    );
};

export default Train;
